// src/components/features/invitations/WelcomePage.js
//
// The page a platform (admin-created account) invitation email links to. This
// is the invitee's FIRST contact with the product, so it renders the welcome
// content — headline, plan name, and the same three capability features as
// the invitation email (functions/invitationEmailShell.js PLATFORM_FEATURES)
// — BEFORE sign-in resolves, then lets them claim their workspace.
//
// Deliberately outside the authenticated app shell: it renders inside
// InvitationShell only, never imports the app shell, the workspace store, or
// any account-bootstrap hook — the callable that every sign-in path in this
// app invokes to provision a workspace on the default billing tier is
// intentionally not referenced anywhere in this file (see
// tests/welcomePage.test.js). If that callable fires before
// acceptPlatformInvitation runs, the customer lands on the wrong plan.
// Registered in src/App.js as a sibling of /login, /privacy, /terms, and
// /accept-invite, above the path="*" catch-all, so mounting this route never
// triggers that bootstrap.
//
// The preview is fetched from the token query parameter on mount without
// waiting for auth to resolve — the whole point is that the welcome content
// renders before sign-in. authUser is tracked only to decide which action the
// primary button performs.
//
// Post-acceptance, this page records a one-shot welcome-tour request through
// ../tours/welcomeTourFlag (a zero-import sessionStorage module) and navigates
// to "/". It does NOT call TourContext's launchTour itself: useTour() throws
// outside a TourProvider, and TourProvider is only mounted inside the
// authenticated app shell this page deliberately avoids importing.
// WelcomeTourLauncher (src/components/features/tours/WelcomeTourLauncher.js),
// mounted inside TourProvider in src/App.js, consumes that request once the
// shell boots and launches the tour from there — the handoff crosses the
// unauthenticated/authenticated boundary through a plain browser API, never
// through a shared import of shell state.

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { GoogleAuthProvider, signInWithPopup, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../../firebase';
import * as platformInvitationService from '../../../services/platformInvitationService';
import { requestWelcomeTour } from '../tours/welcomeTourFlag';
import Button from '../../ui/primitives/Button';
import { usePendingAction } from '../../../hooks/usePendingAction';
import InvitationShell from './InvitationShell';
import InvitationFeatureList from './InvitationFeatureList';
import InvitationStatePanel from './InvitationStatePanel';

import { SUPPORT_EMAIL } from '../../../config/support';

// Same three features and substance as PLATFORM_FEATURES in
// functions/invitationEmailShell.js, so the inbox and this page read as one
// thing. HTML entities from the email template are written as plain Unicode
// here since this is JSX, not a raw HTML email body.
const PLATFORM_FEATURES = [
    {
        icon: 'contracts',
        title: 'Your contracts, read for you',
        body: 'Upload the executed contract. The earnest money, inspection, and homeowners association deadlines are worked out from its day counts. Change the contract date and every deadline counted from it moves too.',
    },
    {
        icon: 'portal',
        title: 'A portal for every client',
        body: 'They stop texting “any update?”, because they can see exactly where their file stands, any time they want.',
    },
    {
        icon: 'commission',
        title: 'Commission math that survives your cap',
        body: 'Splits, company dollar, and the deal that straddles your cap year. Handled.',
    },
];

function useQueryToken() {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search).get('token') || '', [search]);
}

/**
 * Claim the workspace, then record the welcome-tour request.
 *
 * Extracted from the click handler below and exported so the ordering that
 * actually matters can be tested without a DOM, a router, or an async act()
 * — the request must be recorded only after the callable resolves, so a
 * rejected acceptance leaves nothing behind for WelcomeTourLauncher to find.
 * The component's own handler is the only production caller.
 *
 * Rejects with whatever acceptPlatformInvitation threw.
 *
 * @param {string} token
 */
export async function acceptInvitationAndRequestWelcomeTour(token) {
    await platformInvitationService.acceptPlatformInvitation(token);
    requestWelcomeTour();
}

/**
 * @param {{ initialPreview?: object|null, initialAuthUser?: object|null, initialLoadError?: string }} props
 *   The three `initial*` props seed this component's state and exist purely
 *   for deterministic render-smoke testing: renderToStaticMarkup never runs
 *   effects, so the only way to exercise the pending/expired/revoked/accepted/
 *   not_found branches without a jsdom + act render harness is to seed the
 *   state a real mount would eventually reach via the fetch effect below. In
 *   production no caller passes them, so the page always starts from the real
 *   fetch and the real (initially unresolved) auth state.
 */
export default function WelcomePage({ initialPreview = null, initialAuthUser = null, initialLoadError = '' } = {}) {
    const token = useQueryToken();
    const navigate = useNavigate();
    const [authUser, setAuthUser] = React.useState(initialAuthUser);
    const [preview, setPreview] = React.useState(initialPreview);
    const [loadError, setLoadError] = React.useState(initialLoadError);
    const [actionError, setActionError] = React.useState('');

    // Tracked for the button label only — never gates the initial render.
    React.useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, setAuthUser);
        return unsubscribe;
    }, []);

    // Fetches on mount from the token, independent of auth state.
    React.useEffect(() => {
        if (!token) return;
        let cancelled = false;
        (async () => {
            try {
                const data = await platformInvitationService.getPlatformInvitationPreview(token);
                if (!cancelled) setPreview(data);
            } catch (err) {
                if (!cancelled) setLoadError(err?.message || 'Could not load this invitation.');
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    const signIn = async () => {
        setActionError('');
        try {
            await signInWithPopup(auth, new GoogleAuthProvider());
        } catch (err) {
            setActionError(err?.message || 'Sign-in failed.');
        }
    };
    const { run: handleSignIn, pending: signingIn } = usePendingAction(signIn);

    const accept = async () => {
        setActionError('');
        try {
            await acceptInvitationAndRequestWelcomeTour(token);
            navigate('/');
        } catch (err) {
            setActionError(err?.message || 'Failed to accept invitation.');
        }
    };
    const { run: handleAccept, pending: accepting } = usePendingAction(accept);

    if (!token) {
        return (
            <InvitationShell>
                <InvitationStatePanel
                    title="Invalid invitation link"
                    body="This link is missing its invitation token. Check the link from your email, or ask whoever invited you to resend it."
                    tone="danger"
                />
            </InvitationShell>
        );
    }

    if (loadError) {
        return (
            <InvitationShell>
                <InvitationStatePanel
                    title="Could not load this invitation"
                    body={loadError}
                    tone="danger"
                />
            </InvitationShell>
        );
    }

    if (!preview) {
        return (
            <InvitationShell>
                <InvitationStatePanel title="Loading…" body="One moment." />
            </InvitationShell>
        );
    }

    if (preview.status === 'expired') {
        return (
            <InvitationShell>
                <InvitationStatePanel
                    title="Invitation expired"
                    body={`Ask whoever invited you to send a new one, or reach us at ${SUPPORT_EMAIL}.`}
                    tone="danger"
                />
            </InvitationShell>
        );
    }

    if (preview.status === 'revoked') {
        return (
            <InvitationShell>
                <InvitationStatePanel
                    title="Invitation revoked"
                    body="This invitation was revoked. Contact whoever invited you if you still need a workspace."
                    tone="danger"
                />
            </InvitationShell>
        );
    }

    if (preview.status === 'accepted') {
        return (
            <InvitationShell>
                <InvitationStatePanel
                    title="Already claimed"
                    body="This invitation has already been accepted. Sign in to open your workspace."
                    action={{ label: 'Sign in', href: '/login' }}
                />
            </InvitationShell>
        );
    }

    if (preview.status === 'not_found') {
        return (
            <InvitationShell>
                <InvitationStatePanel
                    title="Invitation not found"
                    body={`This link doesn't match an invitation. Double-check the link from your email, ask whoever invited you to resend it, or reach us at ${SUPPORT_EMAIL}.`}
                    tone="danger"
                />
            </InvitationShell>
        );
    }

    // status === 'pending'
    return (
        <InvitationShell>
            <h1 className="text-2xl font-semibold mb-2 text-[color:var(--text-primary)]">
                Welcome to KeyFlow, {preview.inviteeFirstName}.
            </h1>
            <p className="text-sm text-[color:var(--text-muted)] mb-1">
                Your workspace is built, set up on the {preview.tierName} plan, and waiting for you.
            </p>
            <p className="text-sm text-[color:var(--text-muted)] mb-6">
                Invitation sent to <strong>{preview.inviteeEmail}</strong>.
            </p>
            <InvitationFeatureList features={PLATFORM_FEATURES} />
            {actionError && (
                <p className="text-sm text-[color:var(--color-danger)] mt-4">{actionError}</p>
            )}
            <Button
                variant="accent"
                onClick={authUser ? handleAccept : handleSignIn}
                loading={authUser ? accepting : signingIn}
                loadingLabel={authUser ? 'Accepting…' : 'Signing in…'}
                className="mt-6 w-full"
            >
                {authUser ? 'Accept and open my workspace' : 'Sign in with Google to claim'}
            </Button>
        </InvitationShell>
    );
}
